import type { FC, ReactElement } from 'react';

import QueryHandler from '@zen/Components/QueryHandler';
import Slideout, { SlideoutHeader } from '@zen/Components/Slideout';
import { Loading } from '@zen/DesignSystem';

import { type AuditLogsFilters, useAuditLogsGrouped } from '../hooks/useAuditLogsGroups';
import AuditLogGroup from './AuditLogGroup';
import type { GroupedEntry } from './types';

interface AuditLogsProps {
  isVisible: boolean;
  onClose: () => void;
  preFilters?: AuditLogsFilters;
  title?: string;
}

const AuditLogs: FC<AuditLogsProps> = (props) => {
  const { isVisible, onClose, preFilters, title } = props;

  const { groupedLogs, isLoading, error } = useAuditLogsGrouped({ isVisible, preFilters });

  const groupedEntries = Object.entries(groupedLogs);
  const hasLogs = groupedEntries.length > 0;

  const renderAuditLogGroups = (entries: GroupedEntry[]): ReactElement => (
    <>
      {entries.map(([dateLabel, logs], index) => {
        const isFirstElement = index === 0;

        return <AuditLogGroup key={dateLabel} dateLabel={dateLabel} auditLogs={logs} isExpandedInitially={isFirstElement} />;
      })}
    </>
  );

  return (
    <Slideout
      className="w-1/2 flex flex-col h-full top-0!"
      onOutsideClick={onClose}
      show={isVisible}
      overlay={true}
      unmountOnClose={true}
    >
      <SlideoutHeader onClose={onClose} title={title} />

      <div className="flex-1 overflow-y-auto p-6 pb-20">
        <QueryHandler
          data={hasLogs ? groupedEntries : null}
          error={!!error}
          errorComponent={<p className="text-red-base text-sm">Error loading audit logs.</p>}
          isLoading={isLoading}
          loadingComponent={<Loading />}
          noResults={<p className="text-grey-base text-sm">No audit logs found.</p>}
        >
          {renderAuditLogGroups}
        </QueryHandler>
      </div>
    </Slideout>
  );
};

export default AuditLogs;
